/**
 * SummaryTab Component
 *
 * Executive summary view of an investment with professional formatting
 * Displays all key investment information in a single-page overview
 */

import {
  useMilestones,
  useSponsors,
  useSuccessMeasures,
  useCosts,
  useBenefits,
  useSponsorTypes,
  useMeasureTypes,
  useCostCategories,
} from '@medsuite/api-client';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import type { Investment } from '@medsuite/models';

interface SummaryTabProps {
  investment: Investment;
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format success measure amount based on type
 */
function formatSuccessMeasureAmount(amount: number, amountType: string | null): string {
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

  switch (amountType) {
    case 'D':
      return formatCurrency(amount);
    case 'P':
      return `${formattedNumber}%`;
    case 'Y':
      return `${formattedNumber} Days`;
    case 'H':
      return `${formattedNumber} Headcount`;
    case 'M':
      return `${formattedNumber} Months`;
    case 'R':
      return `${formattedNumber} Hours`;
    case 'U':
      return `${formattedNumber} Units`;
    case 'W':
      return `${formattedNumber} Weeks`;
    default:
      return formatCurrency(amount);
  }
}

/**
 * Format date
 */
function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return '-';
  }
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ investment }) => {
  // Fetch related data
  const { data: milestones = [] } = useMilestones(investment.investment_id);
  const { data: sponsors = [] } = useSponsors(investment.investment_id);
  const { data: successMeasures = [] } = useSuccessMeasures(investment.investment_id);
  const { data: costs = [] } = useCosts(investment.investment_id);
  const { data: benefits = [] } = useBenefits(investment.investment_id);

  // Fetch lookup data
  const { data: sponsorTypes = [] } = useSponsorTypes();
  const { data: measureTypes = [] } = useMeasureTypes();
  const { data: costCategories = [] } = useCostCategories();

  // Sort milestones by sequence
  const sortedMilestones = [...milestones].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  // Get quantitative benefits (Type 1)
  const quantitativeBenefits = benefits.filter((b) => b.benefit_type === 'Q');
  // Get non-quantitative benefits (Type 2)
  const nonQuantitativeBenefits = benefits.filter((b) => b.benefit_type === 'N');

  // Get sponsor type name
  const getSponsorTypeName = (sponsorTypeId: number) => {
    return (
      sponsorTypes.find((st) => st.sponsor_type_id === sponsorTypeId)?.sponsor_type_name ||
      'Sponsor'
    );
  };

  // Get measure type name
  const getMeasureTypeName = (measureTypeId: number) => {
    return (
      measureTypes.find((mt) => mt.measure_type_id === measureTypeId)?.measure_type_name ||
      'Measure'
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Content */}
      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Side - 2/3 width, content flows naturally */}
          <div className="lg:w-2/3 divide-y divide-gray-400 dark:divide-gray-400">
            {/* Prime Objective */}
            {investment.prime_objective && (
              <div className="pb-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Prime Objective
                </h3>
                <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{investment.prime_objective}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Problem & Context */}
            {investment.problem_context && (
              <div className="py-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Problem / Context
                </h3>
                <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{investment.problem_context}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Improvement Ideas */}
            {investment.improvement_ideas && (
              <div className="py-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Improvement Ideas / Solutions
                </h3>
                <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{investment.improvement_ideas}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Assumptions/Constraints */}
            {investment.assumptions && (
              <div className="py-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Assumptions/Constraints
                </h3>
                <div className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{investment.assumptions}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Success Measures & Benefits */}
            <div className="pt-6">
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-3">
                Success Measures & Benefits
              </h3>
              {successMeasures.length === 0 &&
              quantitativeBenefits.length === 0 &&
              nonQuantitativeBenefits.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No success measures or benefits defined
                </p>
              ) : (
                <table className="w-full">
                  <tbody className="divide-y divide-gray-400 dark:divide-gray-400">
                    {/* Success Measures Section */}
                    {successMeasures.length > 0 && (
                      <>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <td
                            colSpan={3}
                            className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide"
                          >
                            Success Measures
                          </td>
                        </tr>
                        {successMeasures.map((measure) => (
                          <tr
                            key={measure.success_measure_id}
                            className="bg-white dark:bg-gray-800"
                          >
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 align-top w-1/4">
                              {getMeasureTypeName(measure.measure_type_id)}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 align-top">
                              {measure.title || '-'}
                              {measure.alternate_value && (
                                <span className="text-gray-500 dark:text-gray-400">
                                  {' '}
                                  – {measure.alternate_value}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 text-right align-top w-28">
                              {(measure.amount ?? 0) > 0 &&
                                formatSuccessMeasureAmount(measure.amount!, measure.amount_type)}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Quantitative Benefits Section */}
                    {quantitativeBenefits.length > 0 && (
                      <>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <td
                            colSpan={3}
                            className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide"
                          >
                            Quantitative Benefits
                          </td>
                        </tr>
                        {quantitativeBenefits.map((benefit) => (
                          <tr
                            key={benefit.investment_benefit_id}
                            className="bg-white dark:bg-gray-800"
                          >
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 align-top w-1/4">
                              {benefit.title || 'Benefit'}
                              {benefit.is_one_time && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200">
                                  One-time
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 align-top">
                              {benefit.description || '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 text-right align-top w-28">
                              {(benefit.amount ?? 0) > 0 && formatCurrency(benefit.amount!)}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Qualitative Benefits Section */}
                    {nonQuantitativeBenefits.length > 0 && (
                      <>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <td
                            colSpan={3}
                            className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide"
                          >
                            Qualitative Benefits
                          </td>
                        </tr>
                        {nonQuantitativeBenefits.map((benefit) => (
                          <tr
                            key={benefit.investment_benefit_id}
                            className="bg-white dark:bg-gray-800"
                          >
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 align-top w-1/4">
                              {benefit.title || 'Benefit'}
                            </td>
                            <td
                              colSpan={2}
                              className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 align-top"
                            >
                              {benefit.description || '-'}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Right Side - 1/3 width, contains middle and right content */}
          <div className="lg:w-1/3 lg:border-l lg:border-gray-400 lg:dark:border-gray-400 lg:pl-8 divide-y divide-gray-400 dark:divide-gray-400">
            {/* Regulatory Details */}
            {investment.is_regulatory_requirement && investment.regulatory_details && (
              <div className="pb-6">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                    Regulatory Details
                  </h4>
                  <div className="text-xs text-red-700 dark:text-red-300 prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{investment.regulatory_details}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {/* Key Milestones */}
            <div className="py-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="px-3 py-2 text-left text-sm font-semibold">Key Milestones</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Expected Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-400 dark:divide-gray-400">
                  {sortedMilestones.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic"
                      >
                        No milestones defined
                      </td>
                    </tr>
                  ) : (
                    sortedMilestones.map((milestone) => (
                      <tr key={milestone.milestone_id} className="bg-white dark:bg-gray-800">
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                          {milestone.title}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 text-right">
                          {formatDate(milestone.expected_date)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Investment Costs */}
            <div className="py-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="px-3 py-2 text-left text-sm font-semibold">Investment Costs</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-400 dark:divide-gray-400">
                  {costs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic"
                      >
                        No costs defined
                      </td>
                    </tr>
                  ) : (
                    costs.map((cost) => {
                      const categoryName = costCategories.find(
                        (c) => c.cost_category_id === cost.cost_category_id
                      )?.cost_category_name;
                      const totalCost = (cost.capex_cost || 0) + (cost.opex_cost || 0);
                      return (
                        <tr key={cost.investment_cost_id} className="bg-white dark:bg-gray-800">
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 align-top">
                            <div>{cost.cost_name || 'Cost'}</div>
                            {categoryName && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {categoryName}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 text-right align-top">
                            {totalCost > 0 && (
                              <>
                                <div>{formatCurrency(totalCost)}</div>
                                {(cost.capex_cost ?? 0) > 0 && (cost.opex_cost ?? 0) > 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    CapEx: {formatCurrency(cost.capex_cost!)}
                                    <br />
                                    OpEx: {formatCurrency(cost.opex_cost!)}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Sponsors */}
            <div className="pt-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="px-3 py-2 text-left text-sm font-semibold">Sponsors</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Name / Title</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-400 dark:divide-gray-400">
                  {sponsors.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic"
                      >
                        No sponsors defined
                      </td>
                    </tr>
                  ) : (
                    sponsors.map((sponsor) => (
                      <tr key={sponsor.sponsor_id} className="bg-white dark:bg-gray-800">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 align-top">
                          {getSponsorTypeName(sponsor.sponsor_type_id)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 align-top">
                          <div>{sponsor.sponsor_name}</div>
                          {sponsor.sponsor_title && (
                            <div className="text-gray-500 dark:text-gray-400">
                              {sponsor.sponsor_title}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
